import { useId, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { startPathFor } from '@/features/auth/guest';
import { trackCta } from '@/lib/analytics';
import { Book, Brand, Button, Calendar, Clipboard, Home, Lines, Menu, Sheet, VisuallyHidden } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { AccountMenu } from './AccountMenu';
import { LanguageToggle } from './LanguageToggle';
import { SINGLE_FLOW } from '@/features/flow';
import { Footer } from './Footer';

/** Primary destinations for a signed-in parent — the same set on desktop and in the phone tab bar. */
const ALL_PRIMARY = [
  { to: '/home', key: 'nav.home', Icon: Home },
  { to: '/reports', key: 'nav.reports', Icon: Lines },
  { to: '/programme', key: 'nav.programme', Icon: Calendar },
  { to: '/children', key: 'nav.checkins', Icon: Clipboard },
  { to: '/learn', key: 'nav.learn', Icon: Book },
] as const;

/**
 * One flow in Phase 0, so the programme is reached through its report rather
 * than browsed on its own. Hidden, not removed — see `features/flow.ts`.
 */
const PRIMARY = SINGLE_FLOW
  ? ALL_PRIMARY.filter((item) => item.to !== '/programme')
  : ALL_PRIMARY;

const SECONDARY = [
  { to: '/family', key: 'nav.family' },
  { to: '/transactions', key: 'nav.transactions' },
  { to: '/settings', key: 'nav.settings' },
] as const;

/** Signed-out links, each to its own page. Pricing lives in the footer, next to the legal links. */
const PUBLIC = [
  { href: '/home-plan', key: 'nav.homePlan' },
  { href: '/how-it-works', key: 'nav.howItWorks' },
  { href: '/learn', key: 'nav.learn' },
  { href: '/trust', key: 'nav.trust' },
] as const;

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cx(
    'inline-flex min-h-tap items-center whitespace-nowrap rounded-md px-3 text-label font-bold transition-colors duration-fade',
    isActive ? 'bg-lagoon-100 text-lagoon-700' : 'text-ink-soft hover:bg-mist hover:text-lagoon-600',
  );

/** Rows in the phone menu: full width, one line each, a hairline between them. */
const sheetLinkClass = ({ isActive }: { isActive: boolean }) =>
  cx(
    'flex min-h-tap items-center rounded-md px-3 text-label font-bold transition-colors duration-fade',
    isActive ? 'bg-lagoon-100 text-lagoon-700' : 'text-ink hover:bg-mist hover:text-lagoon-600',
  );

export interface AppShellProps {
  children: ReactNode;
  /** Full-bleed main (landing). Sections then set their own width. */
  wide?: boolean;
}

export const AppShell = ({ children, wide = false }: AppShellProps) => {
  const t = useT();
  const { account: session, config, signOut } = useSession();
  // A guest (a check-in started without an account) sees the visitor's navigation: there is no
  // home, report or settings to go to until they sign up.
  const account = session?.guest ? null : session;
  const startTo = session?.guest ? '/signup' : startPathFor(session, config);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();

  const handleSignOut = () => {
    signOut();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="skip-link">
        {t('app.skip')}
      </a>

      <header className="sticky top-0 z-30 border-b border-line bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
        <div className="mx-auto flex h-[68px] max-w-content items-center gap-6 px-4 md:px-6">
          <Link to={account ? '/home' : '/'} className="rounded-md" aria-label={`${t('app.name')} ${t('app.by')}`}>
            <Brand />
          </Link>

          <nav aria-label={t('nav.primary')} className="hidden flex-1 items-center gap-1 nav:flex">
            {account
              ? PRIMARY.map((item) => (
                  <NavLink key={item.to} to={item.to} className={navLinkClass}>
                    {t(item.key)}
                  </NavLink>
                ))
              : PUBLIC.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={(state) => cx(navLinkClass(state), 'wideOnly' in item && 'hidden min-[1400px]:inline-flex')}
                  >
                    {t(item.key)}
                  </NavLink>
                ))}
            <span className="ml-auto flex items-center gap-2">
              {account ? (
                <AccountMenu
                  name={account.name}
                  links={SECONDARY.map((item) => ({ to: item.to, label: t(item.key) }))}
                  onSignOut={handleSignOut}
                />
              ) : (
                <>
                  <Link to="/signin" className={navLinkClass({ isActive: false })}>
                    {t('nav.signin')}
                  </Link>
                  <Button as="a" href={startTo} className="whitespace-nowrap" onClick={(e) => { e.preventDefault(); trackCta('nav_start'); navigate(startTo); }}>
                    {t(session?.guest ? 'guest.saveCta' : 'nav.begin')}
                  </Button>
                </>
              )}
              {/* Last on the right, and small: most parents never need it, those who do find it at the edge. */}
              <LanguageToggle compact />
            </span>
          </nav>

          <button
            type="button"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen(true)}
            className="ml-auto inline-flex min-h-tap min-w-tap items-center justify-center rounded-md text-ink nav:hidden"
          >
            <Menu />
            <VisuallyHidden>{t('nav.menu')}</VisuallyHidden>
          </button>
        </div>
      </header>

      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title={t('nav.menu')}>
        <nav id={menuId} aria-label={t('app.name')} className="flex flex-col">
          {account ? (
            <>
              {[...PRIMARY, ...SECONDARY].map((item) => (
                <NavLink key={item.to} to={item.to} onClick={() => setMenuOpen(false)} className={sheetLinkClass}>
                  {t(item.key)}
                </NavLink>
              ))}
            </>
          ) : (
            PUBLIC.map((item) => (
              <NavLink key={item.href} to={item.href} onClick={() => setMenuOpen(false)} className={sheetLinkClass}>
                {t(item.key)}
              </NavLink>
            ))
          )}
        </nav>
        <div className="mt-2 flex items-center justify-between gap-3 border-t border-line px-3 pt-3">
          <span className="text-label font-bold text-ink-soft">{t('nav.language')}</span>
          <LanguageToggle />
        </div>
        <div className="mt-4">
          {account ? (
            <Button variant="secondary" full onClick={handleSignOut}>
              {t('nav.signout')}
            </Button>
          ) : (
            <div className="grid gap-2">
              <Button full onClick={() => { setMenuOpen(false); trackCta('menu_start'); navigate(startTo); }}>
                {t(session?.guest ? 'guest.saveCta' : 'nav.begin')}
              </Button>
              <Button variant="secondary" full onClick={() => { setMenuOpen(false); navigate('/signin'); }}>
                {t('nav.signin')}
              </Button>
            </div>
          )}
        </div>
      </Sheet>

      <main
        id="main"
        className={cx('w-full flex-1', wide ? '' : 'mx-auto max-w-content px-4 py-6 md:px-6 md:py-8', account && 'pb-24 nav:pb-8')}
      >
        {children}
      </main>

      <Footer />

      {account && (
        <nav
          aria-label={t('nav.tabs')}
          className="fixed inset-x-0 bottom-0 z-30 grid border-t border-line bg-white px-1 pt-1.5 nav:hidden"
          style={{ gridTemplateColumns: `repeat(${PRIMARY.length}, minmax(0, 1fr))`, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6px)' }}
        >
          {PRIMARY.map(({ to, key, Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cx('flex min-h-tap flex-col items-center justify-center gap-0.5 rounded-md text-[11.5px] font-extrabold', isActive ? 'text-lagoon-600' : 'text-ink-muted')
              }
            >
              <Icon className="h-[22px] w-[22px]" />
              {t(key)}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
};
